import { Edit2, Save, X, User, Users } from 'lucide-react'
import { useState, useEffect } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  useIssueAssignmentsList,
  useUpdateAssignments,
} from '@/hooks/pm'

import { AssigneeSelector } from './assignee-selector'

// ==================== Types ====================

interface IssueAssignmentPanelProps {
  issueId: string
  projectId: string
  disabled?: boolean
  onSuccess?: () => void
}

// ==================== Component ====================

/**
 * 任务分配面板组件
 * - 显示当前 owner 和 participants
 * - 支持编辑模式切换
 * - 实现保存和取消操作
 */
export function IssueAssignmentPanel({
  issueId,
  projectId,
  disabled = false,
  onSuccess,
}: IssueAssignmentPanelProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [ownerId, setOwnerId] = useState<string>('')
  const [participantIds, setParticipantIds] = useState<string[]>([])

  const { data: assignments, isLoading } = useIssueAssignmentsList(issueId)
  const updateMutation = useUpdateAssignments()

  // 初始化编辑状态
  useEffect(() => {
    if (assignments) {
      const owner = assignments.find((a) => a.role === 'owner')
      const participants = assignments.filter((a) => a.role === 'participant')
      setOwnerId(owner?.employeeId || '')
      setParticipantIds(participants.map((p) => p.employeeId))
    }
  }, [assignments])

  // 获取当前 owner
  const currentOwner = assignments?.find((a) => a.role === 'owner')

  // 获取当前 participants
  const currentParticipants = assignments?.filter((a) => a.role === 'participant') || []

  // 进入编辑模式
  const handleEdit = () => {
    setIsEditing(true)
  }

  // 取消编辑
  const handleCancel = () => {
    // 重置为原始值
    if (assignments) {
      const owner = assignments.find((a) => a.role === 'owner')
      const participants = assignments.filter((a) => a.role === 'participant')
      setOwnerId(owner?.employeeId || '')
      setParticipantIds(participants.map((p) => p.employeeId))
    }
    setIsEditing(false)
  }

  // 保存分配
  const handleSave = () => {
    updateMutation.mutate(
      {
        issueId,
        data: {
          ownerId: ownerId || null,
          participantIds,
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false)
          onSuccess?.()
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-20 rounded bg-muted" />
          <div className="h-8 w-full rounded bg-muted" />
          <div className="h-4 w-20 rounded bg-muted" />
          <div className="h-8 w-full rounded bg-muted" />
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border p-4">
      {/* 标题和操作按钮 */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-medium">任务分配</h3>
        {!isEditing ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEdit}
            disabled={disabled}
            className="h-8 gap-1"
          >
            <Edit2 className="h-3.5 w-3.5" />
            编辑
          </Button>
        ) : (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
              className="h-8 gap-1"
            >
              <X className="h-3.5 w-3.5" />
              取消
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="h-8 gap-1"
            >
              <Save className="h-3.5 w-3.5" />
              {updateMutation.isPending ? '保存中...' : '保存'}
            </Button>
          </div>
        )}
      </div>

      {/* 负责人 */}
      <div className="mb-4">
        <label className="mb-1.5 flex items-center gap-1 text-sm text-muted-foreground">
          <User className="h-3.5 w-3.5" />
          负责人
        </label>
        {isEditing ? (
          <AssigneeSelector
            projectId={projectId}
            mode="single"
            value={ownerId}
            onChange={(value) => setOwnerId(value as string)}
            placeholder="选择负责人"
            excludeIds={participantIds}
          />
        ) : (
          <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
            {currentOwner ? (
              <>
                <User className="h-4 w-4" />
                <span>{currentOwner.employeeName}</span>
              </>
            ) : (
              <span className="text-muted-foreground">未分配</span>
            )}
          </div>
        )}
      </div>

      {/* 参与者 */}
      <div>
        <label className="mb-1.5 flex items-center gap-1 text-sm text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          参与者
        </label>
        {isEditing ? (
          <AssigneeSelector
            projectId={projectId}
            mode="multiple"
            value={participantIds}
            onChange={(value) => setParticipantIds(value as string[])}
            placeholder="选择参与者"
            excludeIds={ownerId ? [ownerId] : []}
          />
        ) : (
          <div className="flex flex-wrap gap-1 rounded-md border bg-muted/50 px-3 py-2 text-sm">
            {currentParticipants.length > 0 ? (
              currentParticipants.map((p) => (
                <Badge key={p.id} variant="secondary">
                  {p.employeeName}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">无参与者</span>
            )}
          </div>
        )}
      </div>

      {/* 错误提示 */}
      {updateMutation.error && (
        <div className="mt-3 rounded-md bg-destructive/10 p-2 text-sm text-destructive">
          {updateMutation.error.message}
        </div>
      )}
    </div>
  )
}
